import type { ParseKeys } from "i18next";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import styles from "./buy-electricity.module.scss";
import CustomerPageLayout from "../customer-page-layout/CustomerPageLayout";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../stores/store";
import { CountryCode, countryCurrencies } from "../../../model/common/country";
import { BuyElectricityConfirmation, BuyElectricityForm, BuyElectricityTransactionPin, BorrowTokenForm, AskAFriendForm } from "./component";
import { CopyableRow } from "../../../common/components/copyable-row/CopyableRow";
import {
	BorrowEligibilityData,
	BuyElectricityConfirmationData,
	BuyElectricityFormState,
	BuyElectricityNavigationState,
	PaymentOptions,
	VIEW_FLOW,
	BuyElectricityViewState,
} from "./buy-electricity.types";

import { getTransactionRefFromToken } from "../../../guards/common/authentification-functions";
import { useGetUserInfoQuery } from "../../../service/user-service";
import { updateCustomerInfo } from "../../../stores/slices/current-user-slice";
import { setEnergyProfileInfo } from "../../../stores/slices/customer-energy-profile-slice";
import {
	clearTransaction,
	setPaymentMethod,
	setTransactionDetails,
	setTransactionPaymentType,
	setTransactionReference,
	setTransactionStatus,
} from "../../../stores/slices/buy-electricity-slice";
import { PaymentMethodSelection, CustomerPinModal, PaymentProcessor, SuccessScreen, TransactionResultPage } from "../components";
import { TransactionResultPageV2 } from "../components/transaction-result-v2";
import { useLazyGetPrepaidTransactionByReferenceForCustomerQuery, useLazyDownloadTransactionInvoiceQuery } from "../../../service/transaction-service";
import { useGetEnergyAccountQuery, useLazyVerifyPostpaidMeterQuery, useReVendMutation } from "../../../service/vending-service";
import { useGetPaymentFeeMutation, useGetWalletBalanceQuery } from "../../../service/charge-service";
import { formatCurrency } from "../../../utils";
import { VerifyPostpaidMeterResponse, VerifyPrepaidMeterResponse } from "../../../model/charge";
import { BuyElectricityNotificationFlow } from "./component/notification-flow/BuyElectricityNotificationFlow";
import { SelectPaymentType } from "./component/select-payment-type";
import { ApplicationRoutes } from "../../../routes";
import { useGetBeneficiariesQuery, useSaveBeneficiaryMutation, useDeleteBeneficiaryMutation } from "../../../service/beneficiary-service";
import { useFeatureFlag } from "../../../tenant/features/useFeatureFlag";
import { FeatureFlagName } from "../../../tenant/features/featureFlags";
import { ConsumerType } from "../../../model/energy-profile";
import { getErrorMessage } from "../../../utils/error-handler";
import successGif from '../../../assets/gifs/success-gif.gif';
import { useVendingOutcomeEvents } from "../../../hooks/useVendingOutcomeEvents";
import {
	BillingFailureReason,
	trackBillingFailed,
	trackBillingSuccess,
	trackBillPaymentStarted,
	trackContinueToPay,
	trackPaymentMethodSelected,
} from "../../../services/business-analytics/vendEvents";

const PAYMENT_METHOD_LABEL_KEYS: Record<PaymentOptions, ParseKeys> = {
	[PaymentOptions.Card]: "buyElectricity.web.debitCreditCard",
	[PaymentOptions.Wallet]: "common.labels.wallet",
	[PaymentOptions.Bank]: "buyElectricity.web.bankTransfer",
	[PaymentOptions.Mono]: "buyElectricity.web.openBankingMono",
	[PaymentOptions.BorrowToken]: "buyElectricity.web.borrowUnits",
	[PaymentOptions.Transfer]: "buyElectricity.web.payWithTransfer",
	[PaymentOptions.Cash]: "buyElectricity.web.cash",
	[PaymentOptions.AskAFriendToPay]: "payment.askAFriendToPay",
};

export const BuyElectricityPage: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [searchParams] = useSearchParams();
	const notificationState = location.state as BuyElectricityNavigationState | null;

	// Deep-link entries (billing notification, re-order, borrow shortcut, Payment Hub
	// return) skip the Single/Bulk chooser and go straight to the single-purchase flow.
	const isDeepLink =
		!!notificationState?.meterNumber ||
		!!searchParams.get("meternumber") ||
		!!searchParams.get("token") ||
		!!searchParams.get("reference") ||
		searchParams.get("action") === "borrow";

	const [paymentType, setPaymentType] = useState<"single" | "bulk" | null>(isDeepLink ? "single" : null);
	const bulkElectricityPurchaseOn = useFeatureFlag(FeatureFlagName.BULK_ELECTRICITY_PURCHASE);

	if (notificationState?.meterNumber) {
		return <BuyElectricityNotificationFlow notificationState={notificationState} />;
	}

	if (paymentType === null) {
		return (
			<CustomerPageLayout title={t("common.labels.buyElectricity")} onBack={() => navigate(-1)}>
				<SelectPaymentType
					onSelectSingle={() => setPaymentType("single")}
					onSelectBulk={() => navigate(`/${ApplicationRoutes.CUSTOMER_BULK_PURCHASE}`)}
					showBulk={bulkElectricityPurchaseOn}
				/>
			</CustomerPageLayout>
		);
	}

	return <BuyElectricityNormalFlow />;
};

const BuyElectricityNormalFlow: React.FC = () => {
	const { t } = useTranslation();
	const { data: userInfo } = useGetUserInfoQuery();
	const { data: energyAccountData } = useGetEnergyAccountQuery();
	const { data: walletData } = useGetWalletBalanceQuery();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const energyProfile = useSelector((state: RootState) => state?.customerEnergyProfile);
	const currentTransaction = useSelector((state: RootState) => state.buyElectricity);
	const { countryCode } = useSelector((state: RootState) => state.user);

	// Listen for vending status updates via SignalR
	// useVendingStatusUpdates();

	const [searchParams] = useSearchParams();

	const urlMeterNumber = searchParams.get("meternumber");
	const urlAmount = searchParams.get("amount");
	const hasReOrderParams = !!urlMeterNumber && !!urlAmount;

	const [currentView, setCurrentView] = useState<BuyElectricityViewState>("form");
	const [showResetPin, setShowResetPin] = useState(false);
	const [confirmationData, setConfirmationData] = useState<BuyElectricityConfirmationData | null>(null);
	const [paymentReference, setPaymentReference] = useState<string | null>(null);
	const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOptions>(PaymentOptions.Card);

	// Borrow token state
	const [borrowEligibilityData, setBorrowEligibilityData] = useState<BorrowEligibilityData | null>(null);
	const [borrowAmount, setBorrowAmount] = useState<number | null>(null);

	// Transaction result V2 state
	const [fullTransaction, setFullTransaction] = useState<any | null>(null);
	const [getTransaction, { isLoading: isLoadingTransaction }] = useLazyGetPrepaidTransactionByReferenceForCustomerQuery();
	const reportVendingOutcome = useVendingOutcomeEvents();

	// Save-as-beneficiary / revend / receipt
	const [isBeneficiarySaved, setIsBeneficiarySaved] = useState(false);
	const [saveBeneficiary, { isLoading: isSavingBeneficiary }] = useSaveBeneficiaryMutation();
	const [deleteBeneficiary, { isLoading: isDeletingBeneficiary }] = useDeleteBeneficiaryMutation();
	const [reVend, { isLoading: isRevending }] = useReVendMutation();
	const [downloadReceipt, { isFetching: isDownloadingReceipt }] = useLazyDownloadTransactionInvoiceQuery();
	const savedBeneficiariesOn = useFeatureFlag(FeatureFlagName.SAVED_BENEFICIARIES);
	const isCashPaymentEnabled = useFeatureFlag(FeatureFlagName.CASH_PAYMENT);
	const isAskAFriendToPayEnabled = useFeatureFlag(FeatureFlagName.ASK_A_FRIEND_TO_PAY);
	const [friendRequestData, setFriendRequestData] = useState<{ requestLink: string; recipientEmail: string } | null>(null);
	const { data: beneficiariesData } = useGetBeneficiariesQuery(undefined, { skip: !savedBeneficiariesOn });

	const [formState, setFormState] = useState<BuyElectricityFormState>(() => {
		if (hasReOrderParams) {
			return {
				paymentType: "prepaid",
				meterNumber: urlMeterNumber,
				amount: urlAmount,
				meterValidationData: null,
				showForm: true,
			};
		}
		return {
			paymentType: null,
			meterNumber: energyProfile.meterNumber,
			amount: "",
			meterValidationData: null,
			showForm: false,
		};
	});

	useEffect(() => {
		if (userInfo) {
			dispatch(
				updateCustomerInfo({
					firstName: userInfo.firstName,
					lastName: userInfo.lastName,
					countryCode: userInfo.customerDetails.countryCode,
					phoneNumber: userInfo.phoneNumber,
					hasPin: userInfo.hasPin,
					image: userInfo.image,
					email: userInfo.email,
					customerType: userInfo.customerDetails?.customerType,
				})
			);
		}
	}, [userInfo]);

	useEffect(() => {
		if (energyAccountData && !energyProfile.utilityId) {
			dispatch(
				setEnergyProfileInfo({
					id: energyAccountData.id,
					isDeleted: energyAccountData.isDeleted,
					userId: energyAccountData.userId,
					meterNumber: energyAccountData.meterNumber ?? energyAccountData.accountNumber,
					accountNumber: energyAccountData.accountNumber,
					meterAddress: energyAccountData.meterAddress,
					utilityId: energyAccountData.utilityId,
					utility: energyAccountData.utility ?? null,
					consumerType: energyAccountData.consumerType,
					pendingStatus: energyAccountData.pendingStatus ?? null,
					preferRemoteTokenLoad: energyAccountData.preferRemoteTokenLoad ?? null,
					isNinVerified: energyAccountData.isNinVerified ?? false,
					residencyStatus: energyAccountData.residencyStatus ?? null,
					nin: energyAccountData.nin ?? null,
					createdAt: energyAccountData.createdAt,
					updatedAt: energyAccountData.updatedAt,
				})
			);
		}
	}, [energyAccountData]);

	// The flow activates the first time a meter type is known — not on the Payment Hub return,
	// where the form state starts empty.
	const billPaymentStartedRef = React.useRef(false);
	useEffect(() => {
		if (billPaymentStartedRef.current || !formState.paymentType) return;
		billPaymentStartedRef.current = true;
		trackBillPaymentStarted(formState.paymentType);
	}, [formState.paymentType]);

	useEffect(() => {
		const token = searchParams.get("token");

		if (token) {
			const reference = getTransactionRefFromToken(token);
			if (reference) {
				setPaymentReference(reference);
				dispatch(setTransactionReference(reference));
				setCurrentView("processing");
			}
		}
	}, [searchParams, dispatch]);

	// Borrow-Units shortcut: ?action=borrow comes from the home-page tile.
	// Just jump to borrow-amount; the form itself handles meter entry + verification.
	const borrowShortcutHandledRef = React.useRef(false);
	const [enteredBorrowStandalone, setEnteredBorrowStandalone] = useState(false);
	useEffect(() => {
		const action = searchParams.get("action");
		if (action !== "borrow") return;
		if (borrowShortcutHandledRef.current) return;
		borrowShortcutHandledRef.current = true;
		setEnteredBorrowStandalone(true);
		setSelectedPaymentOption(PaymentOptions.BorrowToken);
		dispatch(setTransactionPaymentType("prepaid"));
		setCurrentView("borrow-amount");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchParams]);

	// When the BorrowTokenForm verifies a meter itself, lift the result into orchestrator state
	// so the downstream PIN step has the meter/customer it needs.
	const handleBorrowMeterVerified = useCallback(
		(data: { meterNumber: string; eligibility: BorrowEligibilityData; response: VerifyPrepaidMeterResponse }) => {
			setBorrowEligibilityData(data.eligibility);
			setConfirmationData({
				paymentType: "prepaid",
				meterNumber: data.meterNumber,
				amount: "0",
				customerName: data.response.accountName ?? "",
				meterAddress: energyProfile.meterAddress,
				phoneNumber: userInfo?.phoneNumber ?? "",
				outstandingBalance: 0,
				outstandingDebt: data.eligibility.outstandingDebt,
				disco: energyProfile?.utility?.name ?? "",
				currency: countryCurrencies[countryCode as CountryCode],
				fee: 0,
				showPreferRemoteTokenLoadOption: data.response.showPreferRemoteTokenLoadOption ?? false,
			});
		},
		[energyProfile.meterAddress, energyProfile.utility?.name, userInfo, countryCode]
	);

	const goToNextView = useCallback(() => {
		const nextView = VIEW_FLOW[currentView]?.next;
		if (nextView) {
			setCurrentView(nextView);
		}
	}, [currentView]);

	const goToPreviousView = useCallback(() => {
		const prevView = VIEW_FLOW[currentView]?.prev;
		if (prevView) {
			setCurrentView(prevView);
		} else {
			navigate(-1);
		}
	}, [currentView, navigate]);

	const getTotalAmount = useCallback((): number => {
		if (!confirmationData) return 0;
		const amount = Number.parseFloat(confirmationData.amount);
		// const fee = confirmationData.fee ?? 0;
		// return amount + fee;
		return amount;
	}, [confirmationData]);

	// Include BorrowToken for all prepaid customers with eligibility (maxBorrow > 0)
	// The disabled state is handled separately based on outstanding debt
	const availablePaymentMethods = useMemo((): PaymentOptions[] => {
		// Wallet is offered only when the customer actually has a wallet.
		const hasWallet = !!walletData?.walletDetails?.length;
		const base: PaymentOptions[] = [PaymentOptions.Card, PaymentOptions.Transfer];
		if (hasWallet) base.push(PaymentOptions.Wallet);
		if (isCashPaymentEnabled) base.push(PaymentOptions.Cash);
		if (isAskAFriendToPayEnabled) base.push(PaymentOptions.AskAFriendToPay);
		// Borrow Units payment option hidden on the payment page.
		// if (formState.paymentType === "prepaid" && borrowEligibilityData && borrowEligibilityData.maximumBorrowAmount > 0) {
		// 	base.push(PaymentOptions.BorrowToken);
		// }
		return base;
	}, [walletData, isCashPaymentEnabled, isAskAFriendToPayEnabled]);

	// Determine if BorrowToken should be disabled due to outstanding debt
	const isBorrowTokenDisabled = useMemo((): boolean => {
		if (!borrowEligibilityData) return false;
		return borrowEligibilityData.outstandingDebt > 0;
	}, [borrowEligibilityData]);

	const handleFormCancel = () => {
		setFormState({
			paymentType: null,
			meterNumber: "",
			amount: "",
			meterValidationData: null,
			showForm: false,
		});
		navigate(-1);
	};

	const handleFormContinue = (data: BuyElectricityConfirmationData, borrowEligibility?: BorrowEligibilityData) => {
		setConfirmationData({
			...data,
			disco: energyProfile.utility?.name ?? "",
			meterAddress: data?.meterAddress ?? energyProfile.meterAddress,
			phoneNumber: userInfo.phoneNumber,
		});
		if (borrowEligibility) {
			setBorrowEligibilityData(borrowEligibility);
		}
		dispatch(
			setTransactionDetails({
				amount: data.amount,
				meterNumber: data.meterNumber,
				utilityId: energyProfile.utilityId,
				fee: data.fee,
			})
		);
		if (formState.paymentType) {
			dispatch(setTransactionPaymentType(formState.paymentType as "prepaid" | "postpaid"));
		}
		trackContinueToPay(data.amount);
		goToNextView();
	};

	const handlePaymentContinue = (paymentMethod: PaymentOptions) => {
		// Validate that BorrowToken is not selected when disabled
		if (paymentMethod === PaymentOptions.BorrowToken && isBorrowTokenDisabled) {
			toast.error(t("buyElectricity.web.outstandingDebtSettleFirst"));
			return;
		}

		setSelectedPaymentOption(paymentMethod);
		dispatch(setPaymentMethod(t(PAYMENT_METHOD_LABEL_KEYS[paymentMethod])));
		trackPaymentMethodSelected(paymentMethod);
		if (paymentMethod === PaymentOptions.BorrowToken) {
			setCurrentView("borrow-amount");
		} else if (paymentMethod === PaymentOptions.AskAFriendToPay) {
			setCurrentView("ask-a-friend-form");
		} else {
			goToNextView();
		}
	};

	const handleBorrowContinue = (amount: number) => {
		setBorrowAmount(amount);
		setCurrentView("pin");
	};

	// When on the PIN screen via the borrow path, back goes to borrow-confirmation, not payment
	const handlePinBack = useCallback(() => {
		if (selectedPaymentOption === PaymentOptions.BorrowToken) {
			setCurrentView("borrow-amount");
		} else {
			goToPreviousView();
		}
	}, [selectedPaymentOption, goToPreviousView]);

	// Get the appropriate back handler based on current view and payment option
	const getBackHandler = useCallback(() => {
		if (currentView === "borrow-amount") {
			return () => navigate(-1);
		}
		if (currentView === "pin" && selectedPaymentOption === PaymentOptions.BorrowToken) {
			return handlePinBack;
		}
		return goToPreviousView;
	}, [currentView, selectedPaymentOption, handlePinBack, goToPreviousView, navigate]);

	const handleBorrowTokenSuccess = useCallback(
		async (reference: string) => {
			// Navigate to success view immediately — TransactionResultPageV2 shows loader
			setCurrentView("success");
			try {
				const result = await getTransaction({ Reference: reference }).unwrap();
				if (result) {
					setFullTransaction(result);
					reportVendingOutcome(result);
				}
			} catch (error) {
				console.error("Failed to fetch transaction:", error);
				toast.error(t("buyElectricity.web.failedToLoadTransactionDetails"));
			}
		},
		[getTransaction, reportVendingOutcome, t]
	);

	const handlePaymentSuccess = async (reference: string) => {
		trackBillingSuccess(currentTransaction.amount, reference);
		// For prepaid transactions, fetch full transaction data and show V2 result page
		if (currentTransaction.paymentType === "prepaid") {
			try {
				const result = await getTransaction({ Reference: reference }).unwrap();
				if (result) {
					setFullTransaction(result);
					reportVendingOutcome(result);
					setCurrentView("success");
				}
			} catch (error) {
				console.error("Failed to fetch transaction:", error);
				toast.error(t("buyElectricity.web.failedToLoadTransactionDetails"));
				setCurrentView("success"); // Fall back to simple success screen
			}
		} else {
			setCurrentView("success");
		}
	};

	const handlePaymentFailure = (reference: string, reason: BillingFailureReason = "payment_failed") => {
		trackBillingFailed(reason, reference);
		dispatch(setTransactionStatus("failed"));
		setCurrentView("failed");
	};

	// Resolve meter number + payment type from whatever source is available
	// (confirmationData is local state lost across payment-hub reloads, fullTransaction comes from API)
	const resolvedMeterNumber = confirmationData?.meterNumber ?? fullTransaction?.vendingDetails?.meterNumber ?? null;
	const resolvedPaymentType: "prepaid" | "postpaid" | null =
		currentTransaction.paymentType ??
		(fullTransaction?.customerEnergyAccount?.consumerType === 2 ? "postpaid" : fullTransaction?.customerEnergyAccount ? "prepaid" : null);

	// Detect whether the purchased meter is already a beneficiary
	useEffect(() => {
		if (!beneficiariesData || !resolvedMeterNumber || !resolvedPaymentType) return;
		const targetType = resolvedPaymentType === "prepaid" ? ConsumerType.Prepaid : ConsumerType.Postpaid;
		const exists = beneficiariesData.some(b => b.identifier === resolvedMeterNumber && b.type === targetType);
		setIsBeneficiarySaved(exists);
	}, [beneficiariesData, resolvedMeterNumber, resolvedPaymentType]);

	const handleSaveBeneficiary = useCallback(async () => {
		if (!resolvedMeterNumber || !resolvedPaymentType) {
			toast.error(t("buyElectricity.web.couldntDetermineMeterType"));
			return;
		}
		const type = resolvedPaymentType === "prepaid" ? ConsumerType.Prepaid : ConsumerType.Postpaid;

		if (isBeneficiarySaved) {
			// Optimistically flip first; find the existing record and delete it on the server.
			const existing = beneficiariesData?.find(b => b.identifier === resolvedMeterNumber && b.type === type);
			if (!existing) {
				setIsBeneficiarySaved(false);
				return;
			}
			setIsBeneficiarySaved(false);
			try {
				await deleteBeneficiary(existing.id).unwrap();
				toast.success(t("buyElectricity.web.removedFromBeneficiaries"));
			} catch (err: any) {
				setIsBeneficiarySaved(true);
				toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.web.failedToRemoveBeneficiary"));
			}
			return;
		}

		setIsBeneficiarySaved(true);
		try {
			await saveBeneficiary({ type, identifier: resolvedMeterNumber }).unwrap();
			toast.success(t("buyElectricity.web.savedToBeneficiaries"));
		} catch (err: any) {
			setIsBeneficiarySaved(false);
			toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.web.failedToSaveBeneficiary"));
		}
	}, [resolvedMeterNumber, resolvedPaymentType, isBeneficiarySaved, beneficiariesData, saveBeneficiary, deleteBeneficiary, t]);

	const handleRevendToken = useCallback(async () => {
		const id = fullTransaction?.vendingDetails?.id;
		if (!id) {
			toast.error(t("buyElectricity.web.transactionDetailsUnavailable"));
			return;
		}
		try {
			await reVend({ vendingDetailsId: id }).unwrap();
			const ref = fullTransaction?.reference ?? currentTransaction.reference;
			if (ref) {
				const refreshed = await getTransaction({ Reference: ref }).unwrap();
				if (refreshed) setFullTransaction(refreshed);
			}
			toast.success(t("buyElectricity.web.tokenRegenerationTriggered"));
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.web.failedToRetryTokenGeneration"));
		}
	}, [fullTransaction, currentTransaction.reference, reVend, getTransaction, t]);

	const handleDownloadReceipt = useCallback(async () => {
		const ref = fullTransaction?.reference ?? currentTransaction.reference;
		if (!ref) return;
		try {
			const url = await downloadReceipt(ref).unwrap();
			if (typeof url === "string" && url) {
				window.open(url, "_blank", "noopener,noreferrer");
			}
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.web.failedToDownloadReceipt"));
		}
	}, [fullTransaction, currentTransaction.reference, downloadReceipt, t]);

	const handleShareReceipt = useCallback(async () => {
		const ref = fullTransaction?.reference ?? currentTransaction.reference;
		if (!ref) return;
		try {
			const url = await downloadReceipt(ref).unwrap();
			if (typeof url !== "string" || !url) return;
			const navAny: any = navigator;
			if (typeof navAny.share === "function") {
				await navAny.share({ title: t("buyElectricity.web.electricityPurchaseReceipt"), url });
				return;
			}
			if (navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(url);
				toast.success(t("buyElectricity.web.receiptLinkCopied"));
				return;
			}
			window.open(url, "_blank", "noopener,noreferrer");
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) ?? t("buyElectricity.web.failedToShareReceipt"));
		}
	}, [fullTransaction, currentTransaction.reference, downloadReceipt, t]);

	const primaryWallet = walletData?.walletDetails?.[0];
	const walletCurrency = countryCode === CountryCode.Nigeria ? "NGN" : "USD";
	const walletBalance = formatCurrency(primaryWallet?.availableBalance ?? 0, { currency: walletCurrency });
	const walletAvailable = primaryWallet?.availableBalance ?? 0;
	return (
		<CustomerPageLayout
			title={
				currentView === "borrow-amount" || (currentView === "pin" && selectedPaymentOption === PaymentOptions.BorrowToken)
					? t("buyElectricity.web.borrowUnits")
					: t("common.labels.buyElectricity")
			}
			onBack={getBackHandler()}
			showBackButton={currentView !== "processing" && currentView !== "failed" && currentView !== "success" && currentView !== "ask-a-friend-sent"}
			headerAction={
				(currentView === "form" || currentView === "confirmation" || currentView === "pin" || currentView === "borrow-amount") ? (
					<div className={styles.walletPill}>
						<span className={styles.walletPillLabel}>{t("common.labels.wallet")}</span>
						<span className={styles.walletPillDivider} />
						<span className={styles.walletPillAmount}>{walletBalance}</span>
					</div>
				) : undefined
			}
		>
			<div className={styles.pageContainer}>
				{(currentView === "form" ||
					currentView === "confirmation" ||
					(currentView === "pin" && selectedPaymentOption !== PaymentOptions.BorrowToken)) && (
					<BuyElectricityForm
						countryCode={countryCode as CountryCode}
						onCancel={handleFormCancel}
						onContinue={handleFormContinue}
						initialFormState={formState}
						onFormStateChange={setFormState}
					/>
				)}

				{/* Confirmation modal (merges amount recap + payment method selection) */}
				{currentView === "confirmation" && confirmationData && (
					<BuyElectricityConfirmation
						data={confirmationData}
						totalAmount={getTotalAmount()}
						onClose={goToPreviousView}
						onPay={handlePaymentContinue}
						availableMethods={availablePaymentMethods}
						isBorrowTokenDisabled={isBorrowTokenDisabled}
						borrowEligibility={borrowEligibilityData}
						walletAvailableBalance={walletAvailable}
					/>
				)}

				{currentView === "ask-a-friend-form" && confirmationData && (
					<AskAFriendForm
						utilityId={energyProfile.utilityId ?? ""}
						meterNumber={confirmationData.meterNumber}
						amount={confirmationData.amount}
						currency={confirmationData.currency}
						paymentType={formState.paymentType as "prepaid" | "postpaid"}
						onSuccess={(requestLink, recipientEmail) => {
							setFriendRequestData({ requestLink, recipientEmail });
							setCurrentView("ask-a-friend-sent");
						}}
					/>
				)}

				{currentView === "ask-a-friend-sent" && friendRequestData && (
					<SuccessScreen
						title={t("payment.requestSent")}
						subtitle={t("payment.requestSentSubtitle", { email: friendRequestData.recipientEmail })}
						buttonText={t("payment.goToHistory")}
						secondaryButtonText={t("common.pages.backToHome")}
						imageSrc={successGif}
						onSecondary={() => {
							dispatch(clearTransaction());
							navigate("/dashboard");
						}}
						onContinue={() => {
							dispatch(clearTransaction());
							navigate("/history");
						}}
					>
						<CopyableRow label={t("payment.link")} value={friendRequestData.requestLink} />
					</SuccessScreen>
				)}

				{/* Borrow Token: amount entry. Renders with or without pre-existing eligibility —
				    when entering via ?action=borrow the form collects + verifies the meter itself.
				    Stays mounted during the "pin" step so the PIN modal opens in place over it. */}
				{(currentView === "borrow-amount" ||
					(currentView === "pin" && selectedPaymentOption === PaymentOptions.BorrowToken)) && (
					<BorrowTokenForm
						eligibilityData={borrowEligibilityData ?? undefined}
						currency={confirmationData?.currency ?? countryCurrencies[countryCode as CountryCode]}
						utilityId={energyProfile.utilityId ?? undefined}
						initialMeterNumber={confirmationData?.meterNumber ?? energyProfile.meterNumber ?? ""}
						initialAmount={borrowAmount ?? undefined}
						onCancel={enteredBorrowStandalone ? () => navigate(-1) : goToPreviousView}
						onBorrow={handleBorrowContinue}
						onMeterVerified={handleBorrowMeterVerified}

					/>
				)}

				{currentView === "pin" && !showResetPin && confirmationData && (
					<BuyElectricityTransactionPin
						confirmationData={{
							utilityId: energyProfile.utilityId,
							meterNumber: confirmationData.meterNumber,
							amount: selectedPaymentOption === PaymentOptions.BorrowToken && borrowAmount !== null ? borrowAmount.toString() : confirmationData.amount,
							paymentType: formState.paymentType as "prepaid" | "postpaid",
							showPreferRemoteTokenLoadOption: confirmationData.showPreferRemoteTokenLoadOption,
						}}
						selectedPaymentOption={selectedPaymentOption}
						handleBack={handlePinBack}
						onPaymentSuccess={handlePaymentSuccess}
						onPaymentFailed={handlePaymentFailure}
						onForgetPin={() => setShowResetPin(true)}
						onBorrowSuccess={handleBorrowTokenSuccess}
					/>
				)}

				{currentView === "pin" && showResetPin && (
					<CustomerPinModal
						initialContent="resetPinContent"
						showResetSuccessScreen
						onClose={() => setShowResetPin(false)}
					/>
				)}

				{currentView === "processing" && paymentReference && (
					<PaymentProcessor
						reference={paymentReference}
						skipRedirect
						onSuccess={handlePaymentSuccess}
						onFailed={handlePaymentFailure}
						onReferenceSet={ref => dispatch(setTransactionReference(ref))}
						statusMessages={{
							processing: { title: t("common.labels.vendingInProgress"), subtitle: t("payment.yourTokenIsOnItsWay") },
						}}
					/>
				)}

				{currentView === "success" && currentTransaction.paymentType === "prepaid" ? (
					<TransactionResultPageV2
						transaction={fullTransaction ?? undefined}
						isLoading={isLoadingTransaction}
						currency={countryCode === CountryCode.Nigeria ? "NGN" : "USD"}
						paymentMethod={currentTransaction.paymentMethod ?? undefined}
						onContinue={() => {
							dispatch(clearTransaction());
							setFullTransaction(null);
							setIsBeneficiarySaved(false);
							navigate("/dashboard");
						}}
						onCancel={() => {
							dispatch(clearTransaction());
							setFullTransaction(null);
							setIsBeneficiarySaved(false);
							navigate("/buy-electricity");
						}}
						onReportIssue={() => {
							navigate("/support/report-issue", {
								state: {
									reference: fullTransaction?.reference,
									amount: fullTransaction?.amount?.value,
									transactionStatus: "completed",
								},
							});
						}}
						onSaveBeneficiary={savedBeneficiariesOn ? handleSaveBeneficiary : undefined}
						isBeneficiarySaved={isBeneficiarySaved}
						isSavingBeneficiary={isSavingBeneficiary || isDeletingBeneficiary}
						onRevendToken={handleRevendToken}
						isRevending={isRevending}
						onDownloadReceipt={handleDownloadReceipt}
						isDownloadingReceipt={isDownloadingReceipt}
						onShareReceipt={handleShareReceipt}
						feePaid={currentTransaction.fee ?? confirmationData?.fee ?? undefined}
					/>
				) : currentView === "success" ? (
					<SuccessScreen
						title={t("payment.paymentSuccessfull")}
						buttonText={t("common.pages.backToHome")}
						subtitle={t("buyElectricity.web.viewTransactionInHistory")}
						imageSrc={successGif}
						onContinue={() => {
							dispatch(clearTransaction());
							navigate("/dashboard");
						}}
					/>
				) : null}

				{currentView === "failed" && (
					<TransactionResultPage
						transaction={{
							status: (currentTransaction.status as "failed" | "pending") || "failed",
							reference: currentTransaction.reference || t("buyElectricity.web.notAvailable"),
							amount: parseFloat(currentTransaction.amount || "0"),
							paymentMethod: currentTransaction.paymentMethod || t("buyElectricity.web.notAvailable"),
							date: currentTransaction.date || new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
							time: currentTransaction.time || new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }),
						}}
						onRetry={() => navigate("/buy-electricity")}
						onDone={() => {
							dispatch(clearTransaction());
							navigate("/dashboard");
						}}
					/>
				)}
			</div>
		</CustomerPageLayout>
	);
};
